import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// WhatsApp shop number (Dutch format, remove leading 0 and prefix with country code 31)
const WHATSAPP_NUMBER = '31614359710';
const SHOP_ADDRESS = 'Ashop Rotterdam';

const SERVICES = [
  { id: 'documents', label: 'Documents', icon: 'fa-file-lines', desc: 'PDF, Word, images', color: 'from-indigo-500 to-blue-600', available: true },
  { id: 'copies', label: 'Photocopies', icon: 'fa-copy', desc: 'Quick B&W or color', color: 'from-blue-500 to-cyan-500', available: true },
  { id: 'lamination', label: 'Lamination', icon: 'fa-shield', desc: 'A6 → A3', color: 'from-emerald-500 to-teal-600', available: true },
  { id: 'scanning', label: 'Scanning', icon: 'fa-scanner', desc: 'Physical → digital PDF', color: 'from-cyan-500 to-blue-500', available: true },
  { id: 'passport', label: 'Passport Photos', icon: 'fa-id-card', desc: 'NL, EU, US specs', color: 'from-purple-500 to-pink-600', available: false },
  { id: 'binding', label: 'Binding', icon: 'fa-book', desc: 'Spiral, thermal, staple', color: 'from-orange-500 to-red-500', available: false },
  { id: 'business_cards', label: 'Business Cards', icon: 'fa-address-card', desc: 'Matte or glossy', color: 'from-slate-700 to-slate-900', available: false },
  { id: 'posters', label: 'Posters / Flyers', icon: 'fa-image', desc: 'A2, A1, custom sizes', color: 'from-pink-500 to-rose-600', available: false },
  { id: 'fax', label: 'Fax Service', icon: 'fa-fax', desc: 'Send documents by fax', color: 'from-gray-500 to-gray-700', available: false },
  { id: 'custom', label: 'Custom Prints', icon: 'fa-shirt', desc: 'T-shirts, mugs, canvas', color: 'from-yellow-500 to-orange-500', available: false },
];

const PAPER_SIZES = ['A4', 'A3', 'A5', 'Letter', 'Legal'];
const PAPER_TYPES = ['Standard 80g', 'Premium 100g', 'Cardstock 200g', 'Glossy Photo'];
const BINDING_OPTIONS = ['None', 'Staple', 'Spiral', 'Thermal', 'Hardcover'];

const Print = () => {
  const fileInputRef = useRef(null);

  // Form state
  const [service, setService] = useState('documents');
  const [files, setFiles] = useState([]); // [{name, url, size}]
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const [color, setColor] = useState('bw'); // bw | color
  const [duplex, setDuplex] = useState(true); // double-sided
  const [paperSize, setPaperSize] = useState('A4');
  const [paperType, setPaperType] = useState('Standard 80g');
  const [binding, setBinding] = useState('None');
  const [quantity, setQuantity] = useState(1);
  const [urgent, setUrgent] = useState(false);

  const [fulfillment, setFulfillment] = useState('pickup');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('in_store');

  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [orderNumber, setOrderNumber] = useState(null);

  // Estimate helper (Netherlands standard: €1 per page — adjust later as needed)
  const estimateEuro = () => {
    if (service === 'documents' || service === 'copies') {
      // €1 per page for B&W, €1.50 for color
      const perPage = color === 'color' ? 1.5 : 1.0;
      return (perPage * quantity).toFixed(2);
    }
    if (service === 'lamination') return (2.5 * quantity).toFixed(2);
    if (service === 'scanning') return (0.5 * quantity).toFixed(2);
    return null;
  };

  const est = estimateEuro();

  const handleFileInput = async (e) => {
    const selected = Array.from(e.target.files || []);
    await uploadFiles(selected);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = Array.from(e.dataTransfer.files || []);
    await uploadFiles(dropped);
  };

  const uploadFiles = async (list) => {
    if (!list.length) return;
    setUploading(true);
    for (const file of list) {
      if (file.size > 20 * 1024 * 1024) {
        toast.error(`${file.name} is over 20 MB — please split or send via WhatsApp.`);
        continue;
      }
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API}/print-jobs/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setFiles((prev) => [...prev, {
          name: res.data.name || file.name,
          url: res.data.url,
          size: res.data.size || file.size,
        }]);
      } catch (err) {
        toast.error(`Upload failed: ${file.name}`);
      }
    }
    setUploading(false);
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const buildWhatsAppLink = () => {
    const svc = SERVICES.find((s) => s.id === service)?.label || service;
    const msg = [
      `Hi Ashop! I'd like to place a print order:`,
      ``,
      `Service: ${svc}`,
      `Quantity: ${quantity}`,
      service === 'documents' || service === 'copies' ? `Color: ${color === 'color' ? 'Color' : 'Black & White'}` : null,
      service === 'documents' || service === 'copies' ? `Sides: ${duplex ? 'Double-sided' : 'Single-sided'}` : null,
      service === 'documents' || service === 'copies' ? `Paper: ${paperSize} · ${paperType}` : null,
      binding !== 'None' && (service === 'documents' || service === 'binding') ? `Binding: ${binding}` : null,
      `Fulfillment: ${fulfillment === 'pickup' ? 'Pickup at store' : `Delivery to: ${deliveryAddress || '(address)'}`}`,
      `Payment: ${paymentMethod === 'in_store' ? 'Pay in store' : 'Online'}`,
      urgent ? `⚡ URGENT — please rush` : null,
      customerName ? `Name: ${customerName}` : null,
      notes ? `Notes: ${notes}` : null,
      ``,
      `I'll send the files here 👇`,
    ].filter(Boolean).join('\n');
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
  };

  const validate = () => {
    if (!customerName.trim()) { toast.error('Please enter your name'); return false; }
    if (!customerPhone.trim()) { toast.error('Please enter your phone number'); return false; }
    if (fulfillment === 'delivery' && !deliveryAddress.trim()) { toast.error('Please enter a delivery address'); return false; }
    return true;
  };

  const submitOrder = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        service_type: service,
        files,
        print_options: {
          color, duplex, paper_size: paperSize, paper_type: paperType, binding,
        },
        quantity,
        fulfillment,
        delivery_address: deliveryAddress,
        payment_method: paymentMethod,
        customer_name: customerName,
        customer_phone: customerPhone,
        customer_email: customerEmail,
        notes,
        urgent,
      };
      const res = await axios.post(`${API}/print-jobs`, payload);
      setOrderNumber(res.data.order_number);
      toast.success('Order submitted! We\'ll be in touch.');
    } catch (err) {
      toast.error('Could not submit order. Please try WhatsApp instead.');
    } finally {
      setSubmitting(false);
    }
  };

  const activeService = SERVICES.find((s) => s.id === service);

  const showPrintOptions = ['documents', 'copies'].includes(service);
  const showBinding = ['documents', 'binding'].includes(service);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100" data-testid="print-page">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 font-semibold" data-testid="back-home-link">
            <i className="fas fa-arrow-left"></i>
            <span>Back home</span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center text-white">
              <i className="fas fa-print"></i>
            </div>
            <span className="font-bold text-gray-800">Ashop Print</span>
          </div>
        </div>
      </div>

      {/* Success card */}
      {orderNumber && (
        <div className="max-w-3xl mx-auto px-4 pt-8" data-testid="order-success">
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-3xl p-8 shadow-2xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur">
                <i className="fas fa-check text-2xl"></i>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest font-bold opacity-80">Order received</p>
                <p className="text-2xl font-bold">Your order is on the way!</p>
              </div>
            </div>
            <p className="text-white/90 text-sm mb-4">
              We&apos;ve got your request. One of us will confirm the final price and next steps shortly.
            </p>
            <div className="bg-white/10 backdrop-blur rounded-2xl p-4 flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-xs uppercase tracking-widest opacity-70">Order number</p>
                <p className="font-mono font-bold text-xl">{orderNumber}</p>
              </div>
              <div className="flex gap-2">
                <a
                  href={buildWhatsAppLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white text-emerald-700 font-bold rounded-xl hover:bg-gray-100"
                >
                  <i className="fab fa-whatsapp"></i> Send files
                </a>
                <button
                  onClick={() => { setOrderNumber(null); }}
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-xl"
                >
                  New order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-4 pt-10 pb-6">
        <p className="text-xs uppercase tracking-[0.3em] text-orange-500 font-bold mb-3">Ashop Print Services</p>
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 leading-tight tracking-tight mb-4">
          Print anything.<br />
          <span className="bg-gradient-to-r from-orange-500 via-pink-500 to-red-500 bg-clip-text text-transparent">
            Pickup or delivery.
          </span>
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl leading-relaxed">
          Upload your files here, send them via WhatsApp, or drop by the shop. We handle documents,
          copies, passport photos, lamination, binding, business cards, posters and more.
        </p>
      </div>

      {/* Fast lane WhatsApp */}
      <div className="max-w-6xl mx-auto px-4 mb-8">
        <a
          href={buildWhatsAppLink()}
          target="_blank"
          rel="noopener noreferrer"
          className="group relative overflow-hidden flex items-center justify-between gap-4 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white rounded-3xl p-6 shadow-xl transition-all"
          data-testid="whatsapp-fast-lane"
        >
          <div className="flex items-center gap-4 relative z-10">
            <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center">
              <i className="fab fa-whatsapp text-3xl"></i>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest opacity-90 font-bold">Fastest way</p>
              <p className="text-xl sm:text-2xl font-bold">Send via WhatsApp</p>
              <p className="text-xs sm:text-sm text-white/80">Just message us your files, tell us how many copies. Done.</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 relative z-10 opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="font-bold">Open chat</span>
            <i className="fas fa-arrow-right"></i>
          </div>
          <div className="absolute -right-8 -bottom-8 w-40 h-40 bg-white/10 rounded-full"></div>
          <div className="absolute -right-16 -top-8 w-32 h-32 bg-white/5 rounded-full"></div>
        </a>
      </div>

      {/* Service Picker */}
      <div className="max-w-6xl mx-auto px-4 mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold text-gray-800">1. Pick a service</h2>
          <span className="text-xs text-gray-400">Tap any card</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="services-grid">
          {SERVICES.map((s) => {
            const selected = service === s.id;
            const disabled = !s.available;
            return (
              <button
                key={s.id}
                onClick={() => { if (!disabled) setService(s.id); }}
                disabled={disabled}
                className={`relative p-4 rounded-2xl text-left transition-all border-2 ${
                  selected
                    ? 'border-orange-500 bg-orange-50 shadow-lg scale-105'
                    : disabled
                    ? 'border-gray-200 bg-gray-50 opacity-70 cursor-not-allowed'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
                }`}
                data-testid={`service-${s.id}-btn`}
              >
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center text-white mb-3 ${disabled ? 'grayscale' : ''}`}>
                  <i className={`fas ${s.icon} text-lg`}></i>
                </div>
                <p className="font-bold text-gray-800 text-sm leading-tight">{s.label}</p>
                <p className="text-xs text-gray-400 mt-0.5">{s.desc}</p>
                {disabled && (
                  <span className="absolute top-2 right-2 bg-yellow-400 text-yellow-900 text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider shadow">
                    Soon
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* File Upload */}
      <div className="max-w-6xl mx-auto px-4 mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">2. Upload your files <span className="text-sm text-gray-400 font-normal">(optional if bringing physical or sending via WhatsApp)</span></h2>
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`bg-white border-3 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
            dragging ? 'border-orange-500 bg-orange-50' : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50/50'
          }`}
          style={{ borderWidth: '3px' }}
          data-testid="dropzone"
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileInput}
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.webp,.heic"
            data-testid="file-input"
          />
          <div className="w-16 h-16 mx-auto bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center text-white mb-3 shadow-lg">
            <i className="fas fa-cloud-arrow-up text-2xl"></i>
          </div>
          <p className="text-lg font-bold text-gray-800">Drop files here or tap to browse</p>
          <p className="text-sm text-gray-500 mt-1">PDF, Word, Excel, PowerPoint, images · up to 20 MB each</p>
        </div>

        {(uploading || files.length > 0) && (
          <div className="mt-4 space-y-2" data-testid="uploaded-files">
            {uploading && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-blue-700 flex items-center gap-2">
                <i className="fas fa-spinner fa-spin"></i> Uploading...
              </div>
            )}
            {files.map((f, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center text-gray-500">
                  <i className="fas fa-file"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 truncate">{f.name}</p>
                  <p className="text-xs text-gray-400">{(f.size / 1024).toFixed(0)} KB</p>
                </div>
                <button
                  onClick={() => removeFile(i)}
                  className="w-8 h-8 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500"
                  data-testid={`remove-file-${i}`}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Print Options */}
      <div className="max-w-6xl mx-auto px-4 mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-3">3. Options</h2>
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6 space-y-5">
          {/* Selected service summary */}
          <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${activeService.color} flex items-center justify-center text-white`}>
              <i className={`fas ${activeService.icon}`}></i>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold">Service</p>
              <p className="font-bold text-gray-800">{activeService.label}</p>
            </div>
          </div>

          {showPrintOptions && (
            <>
              {/* Color */}
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2">Color</p>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setColor('bw')} className={`px-4 py-3 rounded-xl font-semibold text-sm border-2 flex items-center justify-center gap-2 ${color === 'bw' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-600'}`} data-testid="color-bw-btn">
                    <i className="fas fa-circle text-gray-700"></i> Black & White
                  </button>
                  <button onClick={() => setColor('color')} className={`px-4 py-3 rounded-xl font-semibold text-sm border-2 flex items-center justify-center gap-2 ${color === 'color' ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-600'}`} data-testid="color-color-btn">
                    <i className="fas fa-palette text-pink-500"></i> Color
                  </button>
                </div>
              </div>

              {/* Duplex */}
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2">Sides</p>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setDuplex(false)} className={`px-4 py-3 rounded-xl font-semibold text-sm border-2 ${!duplex ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-600'}`}>
                    Single-sided
                  </button>
                  <button onClick={() => setDuplex(true)} className={`px-4 py-3 rounded-xl font-semibold text-sm border-2 ${duplex ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-600'}`}>
                    Double-sided
                  </button>
                </div>
              </div>

              {/* Paper Size */}
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2">Paper size</p>
                <div className="flex flex-wrap gap-2">
                  {PAPER_SIZES.map((sz) => (
                    <button key={sz} onClick={() => setPaperSize(sz)} className={`px-4 py-2 rounded-lg font-semibold text-sm border-2 ${paperSize === sz ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-600'}`}>
                      {sz}
                    </button>
                  ))}
                </div>
              </div>

              {/* Paper Type */}
              <div>
                <p className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2">Paper type</p>
                <select value={paperType} onChange={(e) => setPaperType(e.target.value)} className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none">
                  {PAPER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </>
          )}

          {showBinding && (
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2">Binding</p>
              <div className="flex flex-wrap gap-2">
                {BINDING_OPTIONS.map((b) => (
                  <button key={b} onClick={() => setBinding(b)} className={`px-4 py-2 rounded-lg font-semibold text-sm border-2 ${binding === b ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-gray-200 text-gray-600'}`}>
                    {b}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quantity */}
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-2">Quantity</p>
            <div className="flex items-center gap-2">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-10 border-2 border-gray-200 rounded-lg font-bold text-lg hover:border-gray-400" data-testid="qty-minus">−</button>
              <input type="number" value={quantity} min={1} onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))} className="w-24 px-4 py-2 border-2 border-gray-200 rounded-lg text-center font-bold text-lg focus:border-orange-500 focus:outline-none" data-testid="qty-input" />
              <button onClick={() => setQuantity(quantity + 1)} className="w-10 h-10 border-2 border-gray-200 rounded-lg font-bold text-lg hover:border-gray-400" data-testid="qty-plus">+</button>
              <span className="text-sm text-gray-400 ml-2">
                {service === 'documents' || service === 'copies' ? 'pages / copies' : 'items'}
              </span>
            </div>
          </div>

          {/* Urgent */}
          <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl border-2 border-gray-200 hover:border-orange-300 transition-colors">
            <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} className="w-5 h-5 accent-orange-500" data-testid="urgent-checkbox" />
            <div className="flex-1">
              <p className="font-bold text-gray-800 flex items-center gap-2">
                <i className="fas fa-bolt text-orange-500"></i> Rush order
              </p>
              <p className="text-xs text-gray-500">Ready in 1 hour · extra fee applies</p>
            </div>
          </label>
        </div>
      </div>

      {/* Fulfillment & Payment */}
      <div className="max-w-6xl mx-auto px-4 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Fulfillment */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-3">4. How to receive it</h3>
          <div className="space-y-2">
            <button onClick={() => setFulfillment('pickup')} className={`w-full text-left p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${fulfillment === 'pickup' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`} data-testid="fulfillment-pickup">
              <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center">
                <i className="fas fa-store"></i>
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-800">Pickup at store</p>
                <p className="text-xs text-gray-500">{SHOP_ADDRESS}</p>
              </div>
              {fulfillment === 'pickup' && <i className="fas fa-check-circle text-orange-500 text-xl"></i>}
            </button>
            <button onClick={() => setFulfillment('delivery')} className={`w-full text-left p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${fulfillment === 'delivery' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`} data-testid="fulfillment-delivery">
              <div className="w-10 h-10 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center">
                <i className="fas fa-truck"></i>
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-800">Delivery</p>
                <p className="text-xs text-gray-500">To your address in Rotterdam</p>
              </div>
              {fulfillment === 'delivery' && <i className="fas fa-check-circle text-orange-500 text-xl"></i>}
            </button>
            {fulfillment === 'delivery' && (
              <input
                type="text"
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                placeholder="Street, house nr, postcode, city"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none mt-2"
                data-testid="delivery-address-input"
              />
            )}
          </div>
        </div>

        {/* Payment */}
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-3">5. Payment</h3>
          <div className="space-y-2">
            <button onClick={() => setPaymentMethod('in_store')} className={`w-full text-left p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${paymentMethod === 'in_store' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`} data-testid="payment-in-store">
              <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-lg flex items-center justify-center">
                <i className="fas fa-hand-holding-dollar"></i>
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-800">Pay in store</p>
                <p className="text-xs text-gray-500">Cash, PIN, or contactless</p>
              </div>
              {paymentMethod === 'in_store' && <i className="fas fa-check-circle text-orange-500 text-xl"></i>}
            </button>
            <button onClick={() => setPaymentMethod('online')} className={`w-full text-left p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${paymentMethod === 'online' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`} data-testid="payment-online">
              <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-lg flex items-center justify-center">
                <i className="fas fa-credit-card"></i>
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-800">Pay online</p>
                <p className="text-xs text-gray-500">iDEAL, card — we&apos;ll send a link</p>
              </div>
              {paymentMethod === 'online' && <i className="fas fa-check-circle text-orange-500 text-xl"></i>}
            </button>
          </div>
        </div>
      </div>

      {/* Contact + Submit */}
      <div className="max-w-6xl mx-auto px-4 mb-16">
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-3">6. Your details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Full name *" className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none" data-testid="customer-name-input" />
            <input type="tel" value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="Phone / WhatsApp *" className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none" data-testid="customer-phone-input" />
            <input type="email" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="Email (optional)" className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none sm:col-span-2" data-testid="customer-email-input" />
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes: special instructions, page ranges, colors..." rows="2" className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none sm:col-span-2 resize-none" data-testid="notes-input" />
          </div>

          {/* Estimate */}
          {est && (
            <div className="mt-4 bg-gradient-to-r from-orange-50 to-pink-50 border border-orange-200 rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-orange-700 font-bold">Rough estimate</p>
                <p className="text-2xl font-bold text-gray-800">€{est}</p>
              </div>
              <p className="text-xs text-gray-500 text-right max-w-xs">Final price confirmed before printing. Includes VAT.</p>
            </div>
          )}

          {/* Submit + WhatsApp */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={submitOrder}
              disabled={submitting}
              className="px-6 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold rounded-2xl hover:from-orange-600 hover:to-red-600 shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="submit-order-btn"
            >
              {submitting ? (<><i className="fas fa-spinner fa-spin"></i> Submitting...</>) : (<><i className="fas fa-paper-plane"></i> Submit Order</>)}
            </button>
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-2xl shadow-lg flex items-center justify-center gap-2"
              data-testid="whatsapp-submit-btn"
            >
              <i className="fab fa-whatsapp text-xl"></i> Or send via WhatsApp
            </a>
          </div>
          <p className="text-xs text-gray-400 mt-3 text-center">
            <i className="fas fa-shield-halved mr-1"></i>
            Your files are stored securely and deleted after your order is completed.
          </p>
        </div>
      </div>

      {/* FAQ */}
      <div className="max-w-4xl mx-auto px-4 mb-16">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Quick answers</h2>
        <div className="space-y-2">
          {[
            { q: 'How long does it take?', a: 'Standard orders: same day if placed before 4 PM. Rush orders: 1 hour.' },
            { q: 'What file types do you accept?', a: 'PDF, Word, Excel, PowerPoint, JPG, PNG, WebP, HEIC. Up to 20 MB per file.' },
            { q: 'How is my file kept private?', a: 'Files are uploaded over HTTPS and deleted after your order is finished. We never share them.' },
            { q: 'Do you print in bulk?', a: 'Yes — 100+ pages get a discount. Business cards, flyers and posters are quoted individually.' },
            { q: 'Can I bring my own paper?', a: 'Absolutely. Just mention it in the notes.' },
          ].map((item, idx) => (
            <details key={idx} className="bg-white border border-gray-200 rounded-xl p-4 group">
              <summary className="cursor-pointer font-semibold text-gray-800 flex items-center justify-between">
                {item.q}
                <i className="fas fa-chevron-down group-open:rotate-180 transition-transform text-gray-400"></i>
              </summary>
              <p className="mt-3 text-sm text-gray-600 leading-relaxed">{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Print;
